import {useEffect, useState} from 'react'
import {Col, Modal, Row} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {useFormik} from 'formik'
import {useLazyQuery, useMutation, useQuery} from '@apollo/client'
import {CREATE_POLICY} from '../../../gql/mutations/policyMutation'
import {toast} from 'react-toastify'
import {IResCreatePolicyApi} from '../../../types/policies'
import {useNavigate} from 'react-router-dom'
import {INIT_CONTACT_INFO, INIT_FORM_MEMBERS} from '../../../helpers/consts'
import {TABS_FORM} from '../../shared/const'
import * as Yup from 'yup'
import {GET_ZIP_INFO} from '../../../gql/queries/leadsQuery'
import {IResZipCode} from '../../../types/quoteTypes'
import {TTabForm} from '../../policies/policiesTypes'
import {PolicyHolderTabModal} from '../../policies/components/PolicyHolderTabModal'
import {AddressInfoTab} from '../../policies/components/AddressInfoTab'
import {RequestConsentTab} from './RequestConsentTab'
import {CREATE_CONTACT, EDIT_CONTACT} from '../../../gql/mutations/contactsMutation'
import {IResCreateContactApi, IResUpdateContactApi} from '../../../types/contacts'
import {IResOneContact, NodeContact} from '../../../types/contactsApi'
import {GET_ONE_CONTACT_API} from '../../../gql/queries/contactsApiQueries'
import {parseId} from '../../../helpers'
import {GET_STATES_EU} from '../../../gql/queries/statesEUQuery'
import {IResStates} from '../../../types/statesEU'
import {getStateEUByCode} from '../../shared/utils'
import {hasPermission} from '../../../../tfi/helpers'
import {ALL_PERMISSIONS} from '../../../../tfi/helpers/consts'
import {useGetUser} from '../../../../tfi/store/userStore'

interface IModalProps {
  keyboard: boolean
  backdrop: string
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  leadId?: string
  isQuote?: boolean
  idContact?: string
}

export const ModalAddEditContact: React.FC<IModalProps> = ({
  keyboard,
  backdrop,
  isOpen,
  onClose,
  refetch,
  leadId,
  isQuote = false,
  idContact,
}) => {
  const intl = useIntl()
  // Permissions
  const permissions = useGetUser((user) => user?.user?.permissions) ?? []
  const canViewSignatureForms = hasPermission(permissions, ALL_PERMISSIONS.VIEW_SIGNATURE_FORMS)

  const {data: dataStates, error: errorStates} = useQuery<IResStates>(GET_STATES_EU)

  const [gqlGetContact, {data: dataGetContact, loading, error: errorGetContact}] =
    useLazyQuery<IResOneContact>(GET_ONE_CONTACT_API, {
      fetchPolicy: 'no-cache',
    })
  const getContact = (id: string) => {
    gqlGetContact({variables: {id: parseId(id)}})
  }
  useEffect(() => {
    //First render of modal
    if (idContact) {
      getContact(idContact)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idContact])

  const [infoEditContact, setInfoEditContact] = useState<NodeContact | null>(null)
  const [contactId, setContactId] = useState<string>('')

  useEffect(() => {
    if (dataGetContact?.contactById) {
      console.log('contactById:' + dataGetContact?.contactById)
      setInfoEditContact(dataGetContact.contactById)
      console.log('infoEditContact:' + infoEditContact)
      setContactId(dataGetContact.contactById.id)
      if (canViewSignatureForms) {
        setEnableRequestConsent(true)
      } else {
        setEnableRequestConsent(false)
      }

      if (dataGetContact?.contactById?.zipCode) getMarketByZip(dataGetContact?.contactById?.zipCode)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataGetContact])

  const [gqlGetMarket, {data: dataZipCode, loading: loadingZipCode, error}] =
    useLazyQuery<IResZipCode>(GET_ZIP_INFO, {
      fetchPolicy: 'no-cache',
    })

  const [gqlCreateP, {data: dataCreate, loading: loadingCreate, error: errorCreate}] =
    useMutation<IResCreateContactApi>(CREATE_CONTACT)

  const [gqlUpdateP, {data: dataUpdate, loading: loadingUpdate, error: errorUpdate}] =
    useMutation<IResUpdateContactApi>(EDIT_CONTACT)

  const [tabForm, setTabForm] = useState<TTabForm>('policy-holder')
  const allowedTabs: TTabForm[] = canViewSignatureForms
    ? ['policy-holder', 'address-info', 'request-consent']
    : ['policy-holder', 'address-info']

  const overrides: Partial<Record<TTabForm, {name?: string; description?: string}>> = {
    'policy-holder': {
      name: intl.formatMessage({id: 'contacts.form.tab.contact', defaultMessage: 'Contact'}),
      // description: 'New description', // opcional
    },
    'address-info': {
      name: intl.formatMessage({id: 'contacts.form.tab.address', defaultMessage: 'Address'}),
      // description: 'New description', // opcional
    },
    'request-consent': {
      name: intl.formatMessage({id: 'contacts.form.tab.request', defaultMessage: 'Request'}),
      // description: 'New description', // opcional
    },
  }
  const fieldToTabMap: Record<string, TTabForm> = {
    firstName: 'policy-holder',
    lastName: 'policy-holder',
    dateBirth: 'policy-holder',
    gender: 'policy-holder',
    phone: 'policy-holder',
    email: 'policy-holder',
    zipCode: 'address-info',
  }

  const filteredTabs = TABS_FORM.filter((tab) => allowedTabs.includes(tab.tabName)).map((tab) => ({
    ...tab,
    ...overrides[tab.tabName],
  }))

  const [enableRequestConsent, setEnableRequestConsent] = useState(false)
  const [requestSended, setRequestSended] = useState(false)

  function mapContactToFormValues(contact: NodeContact) {
    // console.log('mapContactToFormValues:' + contact.address2)
    return {
      fullName: contact.fullName ?? '',
      firstName: contact.firstName ?? '',
      middleName: contact.middleName ?? '',
      lastName: contact.lastName ?? '',
      secondLastName: contact.secondLastName ?? '',
      preferredLenguage: contact.preferredLenguage ?? '',
      dateBirth: contact.dateBirth ?? '', // o new Date(contact.dateBirth) si usas date picker
      gender: contact.gender ?? '',
      email: contact.email ?? '',
      phone: contact.phone ?? '',
      address1: contact.address1 ?? '',
      address2: contact.address2 ?? '',
      city: contact.city ?? '',
      stateEU: '', // este campo no está en NodeContact, debes mapearlo si tienes esa info
      zipCode: contact.zipCode ?? '',
      countyFips: contact.countyFips ?? '',
      immigrationStatus: contact.immigrationStatus ?? '',
      annualIncome1: contact.annualIncome1 || 0,
      annualIncome2: contact.annualIncome2 || 0,
      annualIncome3: contact.annualIncome3 || 0,
      isImportant: contact.isImportant ?? false,
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: infoEditContact
      ? {...INIT_CONTACT_INFO, ...mapContactToFormValues(infoEditContact)}
      : INIT_CONTACT_INFO,
    validationSchema: Yup.object({
      firstName: Yup.string().required(
        intl.formatMessage({id: 'validation.firstName.required', defaultMessage: 'First name is required'})
      ),
      lastName: Yup.string().required(
        intl.formatMessage({id: 'validation.lastName.required', defaultMessage: 'Last name is required'})
      ),
      dateBirth: Yup.date().required(
        intl.formatMessage({id: 'validation.dateOfBirth.required', defaultMessage: 'Date of birth is required'})
      ),
      gender: Yup.string().required(
        intl.formatMessage({id: 'validation.gender.required', defaultMessage: 'Gender is required'})
      ),
      phone: Yup.string().required(
        intl.formatMessage({id: 'validation.phone.required', defaultMessage: 'Phone is required'})
      ),
      email: Yup.string().email(
        intl.formatMessage({id: 'validation.email.invalid', defaultMessage: 'Invalid email'})
      ),
      zipCode: Yup.string().required(
        intl.formatMessage({id: 'validation.zipCode.required', defaultMessage: 'Zip code is required'})
      ),
    }),

    onSubmit: async (v) => {
      const contactsData = {
        fullName: v.firstName + ' ' + v.lastName,
        firstName: v.firstName,
        middleName: v.middleName,
        secondLastName: v.secondLastName,
        lastName: v.lastName,
        dateBirth: v.dateBirth,
        gender: v.gender,
        phone: v.phone,
        email: v.email,
        address1: v.address1,
        address2: v.address2,
        zipCode: v.zipCode,
        stateEUId: v.stateEUId,
        countyFips: v.countyFips,
        preferredLenguage: v.preferredLenguage,
        immigrationStatus: v.immigrationStatus,
        annualIncome1: v.annualIncome1,
        annualIncome2: v.annualIncome2,
        annualIncome3: v.annualIncome3,
        isImportant: v.isImportant,
      }

      if (infoEditContact) {
        console.log('Update contactsData', contactsData)
        gqlUpdateP({
          variables: {id: infoEditContact.id, contactData: contactsData},
        })
      } else {
        console.log('Create contactsData', contactsData)
        gqlCreateP({variables: {contactData: contactsData}})
      }
    },
  })

  useEffect(() => {
    if (errorCreate) {
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: errorCreate.message}
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorCreate])

  useEffect(() => {
    if (!dataCreate) return
    if (dataCreate.createContact?.success) {
      toast.success(
        intl.formatMessage({
          id: 'contacts.form.toast.added',
          defaultMessage: 'Contact added successfully',
        })
      )
      if (canViewSignatureForms) {
        setEnableRequestConsent(true)
        getContact(dataCreate.createContact.contact.id)
        setTabForm('request-consent')
      } else {
        onClose()
      }
    } else if (dataCreate.createContact?.errors?.length > 0) {
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {
            message:
              dataCreate.createContact.errors[0]?.message ||
              intl.formatMessage({
                id: 'contacts.form.toast.somethingWrong',
                defaultMessage: 'Something wrong',
              }),
          }
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCreate])

  useEffect(() => {
    if (!dataUpdate) return
    if (dataUpdate.updateContact?.success) {
      toast.success(
        intl.formatMessage({
          id: 'contacts.form.toast.updated',
          defaultMessage: 'Contact updated successfully',
        })
      )
      if (canViewSignatureForms) {
        setEnableRequestConsent(true)
        setTabForm('request-consent')
      } else {
        onClose()
      }
    } else if (dataUpdate.updateContact?.errors?.length > 0) {
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {
            message:
              dataUpdate.updateContact.errors[0]?.message ||
              intl.formatMessage({
                id: 'contacts.form.toast.somethingWrong',
                defaultMessage: 'Something wrong',
              }),
          }
        )
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpdate])

  const handleChange = (field: string, value: string) => {
    console.log('field:' + field + ' value: ' + value)
    formik.setFieldValue(field, value)
  }
  const handleBlur = (field: string) => {
    formik.setFieldTouched(field, true)
  }

  const getPrevTab = (currentTab: TTabForm): TTabForm | null => {
    const index = allowedTabs.indexOf(currentTab)
    if (index > 0) {
      return allowedTabs[index - 1]
    }
    return null // ya está en el primero
  }

  const getNextTab = (currentTab: TTabForm): TTabForm | null => {
    const index = allowedTabs.indexOf(currentTab)
    if (index >= 0 && index < allowedTabs.length - 1) {
      return allowedTabs[index + 1]
    }
    return null // ya está en el último tab
  }
  const handleNextTab = async () => {
    const nextTab = getNextTab(tabForm)
    if (nextTab) setTabForm(nextTab)
  }
  const handlePrevTab = () => {
    const prevTab = getPrevTab(tabForm)
    if (prevTab) setTabForm(prevTab)
  }

  const handleSubmitAndNext = async () => {
    const errors = await formik.validateForm()
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0]
      const targetTab = fieldToTabMap[firstErrorField]
      // 👇 Marcar el campo con error como "touched"
      formik.setFieldTouched(firstErrorField, true, false)
      if (targetTab) {
        setTabForm(targetTab)
      }
      return
    }

    // Envío del formulario si no hay errores
    formik.handleSubmit()

    if (enableRequestConsent && enableRequestConsent === true) {
      handleNextTab()
    }
  }

  const handleZipCodeLookup = () => {
    if (formik.values.zipCode.length < 3) return
    getMarketByZip(formik.values.zipCode)
  }

  const getMarketByZip = (zipCode: string) => {
    gqlGetMarket({
      variables: {zipCode},
    })
  }

  useEffect(() => {
    handleChange('stateEU.name', '')
    if (!dataZipCode || !dataZipCode?.zipcodeByZip) return

    const id = dataZipCode?.zipcodeByZip.id ?? ''
    const stateId = dataZipCode?.zipcodeByZip.stateId ?? ''
    const stateName = dataZipCode?.zipcodeByZip.stateName ?? ''
    const defaultCountyFips = dataZipCode?.zipcodeByZip.countyFipsAll?.split('|')[0] || ''
    console.log('stateEUId ' + id)

    const stateEuId = getStateEUByCode(dataStates?.allStates, dataZipCode?.zipcodeByZip.stateId)

    handleChange('stateEUId', '' + parseId(stateEuId?.id ?? ''))
    handleChange('stateEU.code', stateId)
    handleChange('stateEU.name', stateName)
    handleChange('countyFips', defaultCountyFips)
  }, [dataZipCode])

  return (
    <Modal
      className='modal-add-policy'
      backdrop='static'
      keyboard={false}
      show={isOpen}
      onHide={onClose}
      size='xl'
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {infoEditContact ? (
            <FormattedMessage id='contacts.form.title.edit' defaultMessage='Edit Contact' />
          ) : (
            <FormattedMessage id='contacts.form.title.create' defaultMessage='Create a new Contact' />
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col
            xl={3}
            lg={4}
            className='d-none d-md-none d-lg-block'
            style={{
              borderRight: '1px solid #eeeef4',
            }}
          >
            <div className='modal-tabsoptions-left'>
              {filteredTabs.map((tb) => (
                <div
                  key={tb.tabName}
                  className='d-flex align-items-center gap-2 p-5 rounded'
                  style={{
                    backgroundColor:
                      tb.tabName === tabForm
                        ? '#e7e7ff'
                        : !enableRequestConsent && tb.tabName === 'request-consent'
                        ? '#eee'
                        : undefined,
                    position: 'relative',
                    maxWidth: '330px',
                    color:
                      !enableRequestConsent && tb.tabName === 'request-consent'
                        ? '#ccc'
                        : 'inherit',
                  }}
                  role='button'
                  onClick={() => {
                    if (loadingCreate) return
                    if (tb.tabName === 'request-consent' && (!canViewSignatureForms || !enableRequestConsent)) return
                    setTabForm(tb.tabName)
                  }}
                >
                  {tb.tabName === tabForm && <div className='tabs-options-left' />}
                  <div>
                    <i className={`${tb.icon} fs-1`} style={{color: 'inherit'}} />
                  </div>
                  <div>
                    <h6 style={{fontSize: '11px'}} className='m-0'>
                      {tb.name}
                    </h6>
                    <p style={{fontSize: '10px'}} className='m-0'>
                      {tb.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Col>
          <Col lg={8} xl={9}>
            <div className='policy-form'>
              {tabForm === 'policy-holder' && (
                <PolicyHolderTabModal
                  onNextStep={handleNextTab}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  values={formik.values}
                  errors={formik.errors}
                  touched={formik.touched}
                  submitCount={formik.submitCount}
                  isContactModule={true}
                  showSearchContact={false}
                />
              )}
              {tabForm === 'address-info' && (
                <AddressInfoTab
                  handleZipCodeLookup={handleZipCodeLookup}
                  onPrev={handlePrevTab}
                  onNext={handleSubmitAndNext}
                  handleChange={handleChange}
                  loadingZipCode={loadingZipCode}
                  dataZipCode={dataZipCode}
                  handleBlur={handleBlur}
                  values={formik.values}
                  errors={formik.errors}
                  touched={formik.touched}
                  isNextSubmit={true}
                />
              )}
              {tabForm === 'request-consent' && enableRequestConsent && canViewSignatureForms && (
                <RequestConsentTab
                  sourceType={'contact'}
                  sourceId={contactId}
                  requestSended={requestSended}
                  onPrev={() => setTabForm('address-info')}
                  onNext={onClose}
                  hasEmail={infoEditContact?.email ? true : false}
                  hasPhone={infoEditContact?.phone ? true : false}
                />
              )}
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  )
}
